import React from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import type { CollapsedPhaseStackData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';

// Compact card that replaces a turn's full phase stack (rounds +
// tools + peers) when the user collapses it via the controls panel.
// Card-deck visual echoes FanCollapsedChip so "collapsed = stack of
// things behind a badge" reads the same everywhere. Click to expand.
export const CollapsedPhaseStackNode: React.FC<NodeProps<CollapsedPhaseStackData>> = ({
  data,
}) => {
  const { darkMode } = useTheme();
  const { togglePhaseStackCollapse } = usePersonaChatUI();
  const opacityStyle = useDagNodeOpacity(data.turnKey, false);

  const backdropBg = darkMode
    ? 'rgba(17, 24, 39, 0.85)'
    : 'rgba(243, 244, 246, 0.95)';
  const backdropBorder = darkMode
    ? 'rgba(107, 114, 128, 0.6)'
    : 'rgba(209, 213, 219, 0.9)';

  const toolLine = `${data.roundCount} ${data.roundCount === 1 ? 'phase' : 'phases'}${
    data.toolCount > 0 ? ` · ${data.toolCount} tools` : ''
  }${data.peerCount > 0 ? ` · ${data.peerCount} peers` : ''}`;

  return (
    <div
      className="nodrag relative"
      style={{ width: 320, ...opacityStyle }}
    >
      {/* Card-deck backdrops — three layers to visually communicate
          "whole turn folded in here." */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-md border pointer-events-none shadow-sm"
        style={{
          transform: 'rotate(-4deg) translate(-4px, 4px)',
          background: backdropBg,
          borderColor: backdropBorder,
          opacity: 0.4,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 rounded-md border pointer-events-none shadow-sm"
        style={{
          transform: 'rotate(-1.5deg) translate(-1px, 2px)',
          background: backdropBg,
          borderColor: backdropBorder,
          opacity: 0.55,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 rounded-md border pointer-events-none shadow-sm"
        style={{
          transform: 'rotate(2deg) translate(3px, 2px)',
          background: backdropBg,
          borderColor: backdropBorder,
          opacity: 0.7,
        }}
      />
      {/* Foreground card — the clickable, content-bearing face. */}
      <div
        className={`relative px-3 py-2 rounded-md border text-[11px] shadow-sm cursor-pointer select-none transition-transform hover:-translate-y-0.5 hover:shadow-md ${
          darkMode
            ? 'bg-gray-800 border-gray-600/80 text-gray-200 hover:border-emerald-500/60'
            : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-500/70'
        }`}
        onClick={() => togglePhaseStackCollapse(data.turnKey)}
        title="Click to expand the full phase stack"
      >
        <Handle
          type="target"
          position={Position.Top}
          id="t"
          className="!opacity-0 !pointer-events-none"
        />
        {/* Name leads — the user's prompt is the differentiator;
            "Stack N" is admin metadata. v0.2 doctrine: dominant
            visual is the differentiating field. */}
        <div className="flex items-center gap-1.5">
          <span className="opacity-70 shrink-0">▸</span>
          <span className="font-semibold truncate flex-1" title={data.name}>
            {data.name}
          </span>
          <span className="ml-auto text-[9px] uppercase tracking-wider opacity-50 shrink-0">
            collapsed
          </span>
        </div>
        <div className="mt-0.5 text-[9px] uppercase tracking-widest font-bold opacity-60">
          Stack {data.turnIndex + 1}
        </div>
        <div className="mt-0.5 text-[10px] opacity-75 font-mono">
          {toolLine}
        </div>
        {(data.totalCostUsd != null || data.totalTokens != null) && (
          <div className="mt-0.5 text-[10px] opacity-60 font-mono">
            {data.totalCostUsd != null &&
              (data.totalCostUsd < 0.001
                ? `$${data.totalCostUsd.toFixed(5)}`
                : `$${data.totalCostUsd.toFixed(4)}`)}
            {data.totalCostUsd != null && data.totalTokens != null ? ' · ' : ''}
            {data.totalTokens != null &&
              `${data.totalTokens.toLocaleString()} tok`}
          </div>
        )}
        <Handle
          type="source"
          position={Position.Bottom}
          id="b"
          className="!opacity-0 !pointer-events-none"
        />
      </div>
    </div>
  );
};

export default CollapsedPhaseStackNode;
